import React, { useState, useEffect, useCallback } from "react";
import "./Trucks.css";
import Layout from "../UI/Layout";
import { collection, addDoc, getDocs, doc, updateDoc, deleteDoc } from "firebase/firestore";
import { db } from "../firebase";

const STATUS_OPTIONS = ["Inactive", "Active", "In Maintenance"];
const FUEL_OPTIONS = ["CNG", "Diesel", "Petrol"];

const emptyForm = {
  amazonId: "", internalId: "", status: "Inactive",
  vin: "", licensePlate: "", make: "", year: "",
  fuelType: "Diesel", scacCode: "", iftaNumber: "",
  hut2026: "", ezPass: "", inspectionDueDate: "",
};

// ── Snackbar ───────────────────────────────────────────
const Snackbar = ({ message, type, visible }) => (
  <div className={`snackbar snackbar-${type} ${visible ? "snackbar-show" : ""}`}>
    <i className={`fas ${type === "success" ? "fa-check-circle" : "fa-exclamation-circle"}`}></i>
    {message}
  </div>
);

// ── Delete Confirmation Modal ──────────────────────────
const DeleteModal = ({ truck, onConfirm, onCancel, deleting }) => (
  <div className="modal-overlay" onClick={onCancel}>
    <div className="modal-box" onClick={(e) => e.stopPropagation()}>
      <div className="modal-icon"><i className="fas fa-trash-alt"></i></div>
      <h3>Delete Truck?</h3>
      <p>
        <strong>{truck.amazonId}</strong> ({truck.internalId}) will be permanently removed.
      </p>
      <div className="modal-actions">
        <button className="modal-cancel" onClick={onCancel}>Cancel</button>
        <button className="modal-confirm" onClick={onConfirm} disabled={deleting}>
          {deleting ? <><i className="fas fa-spinner fa-spin"></i> Deleting…</> : "Delete"}
        </button>
      </div>
    </div>
  </div>
);

// ── FormField ──────────────────────────────────────────
// IMPORTANT: Defined OUTSIDE Trucks so its reference is stable across renders.
// If defined inside, React treats it as a new component type on every render,
// unmounting + remounting the input and causing focus to be lost on each keystroke.
const FormField = ({ label, name, placeholder, type = "text", required, value, onChange, children }) => (
  <div className="truck-form-group">
    <label>{label}{required && <span className="req"> *</span>}</label>
    {children ?? (
      <input
        type={type}
        name={name}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        required={required}
      />
    )}
  </div>
);

// ── Trucks Page ────────────────────────────────────────
const Trucks = () => {
  const [trucks, setTrucks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [snackbar, setSnackbar] = useState({ message: "", type: "success", visible: false });

  const showSnackbar = useCallback((message, type = "success") => {
    setSnackbar({ message, type, visible: true });
    setTimeout(() => setSnackbar((s) => ({ ...s, visible: false })), 3500);
  }, []);

  useEffect(() => {
    const fetchTrucks = async () => {
      setLoading(true);
      try {
        const snapshot = await getDocs(collection(db, "trucks"));
        setTrucks(snapshot.docs.map((d) => ({ id: d.id, ...d.data() }))
          .sort((a, b) => (a.internalId || "").localeCompare(b.internalId || "", undefined, { numeric: true, sensitivity: "base" })));
      } catch (err) {
        console.error(err);
        showSnackbar("Failed to load trucks.", "error");
      }
      setLoading(false);
    };
    fetchTrucks();
  }, [showSnackbar]);

  const filtered = trucks.filter((t) => {
    const q = searchQuery.toLowerCase();
    const matchSearch =
      t.amazonId?.toLowerCase().includes(q) ||
      t.internalId?.toLowerCase().includes(q) ||
      t.vin?.toLowerCase().includes(q) ||
      t.licensePlate?.toLowerCase().includes(q);
    const matchStatus = statusFilter === "All" || t.status === statusFilter;
    return matchSearch && matchStatus;
  });

  // ── Form helpers ───────────────────────────────────────
  const handleFormChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleAddSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const docRef = await addDoc(collection(db, "trucks"), formData);
      setTrucks((prev) => [...prev, { id: docRef.id, ...formData }]);
      setFormData(emptyForm);
      setShowForm(false);
      showSnackbar("Truck added successfully!");
    } catch (err) {
      console.error(err);
      showSnackbar("Failed to add truck.", "error");
    }
    setSaving(false);
  };

  // ── Inline edit ────────────────────────────────────────
  const handleEditClick = (truck) => { setEditingId(truck.id); setEditData({ ...truck }); };
  const handleEditChange = (e) => setEditData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  const handleEditCancel = () => setEditingId(null);

  const handleEditSave = async (id) => {
    setUpdating(true);
    try {
      const { id: _id, ...dataToSave } = editData;
      await updateDoc(doc(db, "trucks", id), dataToSave);
      setTrucks((prev) => prev.map((t) => (t.id === id ? editData : t)));
      setEditingId(null);
      showSnackbar("Truck updated successfully!");
    } catch (err) {
      console.error(err);
      showSnackbar("Failed to update truck.", "error");
    }
    setUpdating(false);
  };

  // ── Delete ─────────────────────────────────────────────
  const handleDeleteConfirm = async () => {
    setDeleting(true);
    try {
      await deleteDoc(doc(db, "trucks", deleteTarget.id));
      setTrucks((prev) => prev.filter((t) => t.id !== deleteTarget.id));
      showSnackbar("Truck deleted.");
    } catch (err) {
      console.error(err);
      showSnackbar("Failed to delete truck.", "error");
    }
    setDeleting(false);
    setDeleteTarget(null);
  };

  const columns = [
    { key: "amazonId", label: "Amazon ID" },
    { key: "internalId", label: "Internal ID" },
    { key: "status", label: "Status" },
    { key: "vin", label: "VIN #" },
    { key: "licensePlate", label: "License Plate" },
    { key: "make", label: "Make" },
    { key: "year", label: "Year" },
    { key: "fuelType", label: "Fuel Type" },
    { key: "scacCode", label: "SCAC Code" },
    { key: "iftaNumber", label: "IFTA #" },
    { key: "hut2026", label: "HUT 2026" },
    { key: "ezPass", label: "EZ Pass" },
    { key: "inspectionDueDate", label: "Inspection Due" },
  ];

  return (
    <Layout activePage="trucks">
      <div className="trucks-page">
        <Snackbar {...snackbar} />
        {deleteTarget && (
          <DeleteModal
            truck={deleteTarget}
            onConfirm={handleDeleteConfirm}
            onCancel={() => setDeleteTarget(null)}
            deleting={deleting}
          />
        )}

        {/* ── Header ── */}
        <div className="trucks-page-header">
          <h1 className="trucks-page-title">Trucks</h1>
          <div className="trucks-top-controls">
            <div className="trucks-search-container">
              <i className="fas fa-search trucks-search-icon"></i>
              <input
                type="text"
                placeholder="Search trucks..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="trucks-search-bar"
              />
            </div>
            <select
              className="status-filter-select"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="All">All Statuses</option>
              {STATUS_OPTIONS.map((s) => <option key={s}>{s}</option>)}
            </select>
            <button
              className="trucks-add-btn"
              onClick={() => { setShowForm(!showForm); setEditingId(null); }}
            >
              <i className={`fas ${showForm ? "fa-times" : "fa-plus"}`}></i>
              {showForm ? " Cancel" : " Add Truck"}
            </button>
          </div>
        </div>

        {/* ── Add Truck Form ── */}
        {showForm && (
          <form className="truck-add-form" onSubmit={handleAddSubmit}>
            <div className="form-section-label">Core Info</div>
            <div className="truck-form-fields">
              <FormField label="Amazon ID" name="amazonId" placeholder="e.g. AMZ-TX-007"
                value={formData.amazonId} onChange={handleFormChange} required />
              <FormField label="Internal ID" name="internalId" placeholder="e.g. FZ-107"
                value={formData.internalId} onChange={handleFormChange} required />
              <FormField label="Status" name="status" value={formData.status} onChange={handleFormChange}>
                <select name="status" value={formData.status} onChange={handleFormChange}>
                  {STATUS_OPTIONS.map((s) => <option key={s}>{s}</option>)}
                </select>
              </FormField>
              <FormField label="Make" name="make" placeholder="e.g. Freightliner"
                value={formData.make} onChange={handleFormChange} />
              <FormField label="Year" name="year" placeholder="e.g. 2022" type="number"
                value={formData.year} onChange={handleFormChange} />
              <FormField label="Fuel Type" name="fuelType" value={formData.fuelType} onChange={handleFormChange}>
                <select name="fuelType" value={formData.fuelType} onChange={handleFormChange}>
                  {FUEL_OPTIONS.map((f) => <option key={f}>{f}</option>)}
                </select>
              </FormField>
            </div>

            <div className="form-section-label">Identification</div>
            <div className="truck-form-fields">
              <FormField label="VIN #" name="vin" placeholder="17-char VIN"
                value={formData.vin} onChange={handleFormChange} />
              <FormField label="License Plate #" name="licensePlate" placeholder="e.g. ABC-1234"
                value={formData.licensePlate} onChange={handleFormChange} />
              <FormField label="SCAC Code" name="scacCode" placeholder="e.g. FXLA"
                value={formData.scacCode} onChange={handleFormChange} />
              <FormField label="IFTA #" name="iftaNumber" placeholder="e.g. IFTA-00123"
                value={formData.iftaNumber} onChange={handleFormChange} />
            </div>

            <div className="form-section-label">Compliance</div>
            <div className="truck-form-fields">
              <FormField label="HUT 2026" name="hut2026" placeholder="e.g. HUT-2026-XYZ"
                value={formData.hut2026} onChange={handleFormChange} />
              <FormField label="EZ Pass" name="ezPass" placeholder="e.g. EZ-998877"
                value={formData.ezPass} onChange={handleFormChange} />
              <FormField label="Annual Inspection Sticker Due Date" name="inspectionDueDate"
                type="date" value={formData.inspectionDueDate} onChange={handleFormChange} />
            </div>

            <div className="form-submit-row">
              <button type="submit" className="truck-submit-btn" disabled={saving}>
                {saving
                  ? <><i className="fas fa-spinner fa-spin"></i> Saving…</>
                  : <><i className="fas fa-plus"></i> Add Truck</>}
              </button>
            </div>
          </form>
        )}

        {/* ── Truck Table ── */}
        <div className="trucks-table-wrapper">
          {loading ? (
            <div className="trucks-loading">
              <i className="fas fa-spinner fa-spin"></i> Loading trucks…
            </div>
          ) : (
            <table className="trucks-table">
              <thead>
                <tr>
                  <th>#</th>
                  {columns.map((c) => <th key={c.key}>{c.label}</th>)}
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((truck, index) => (
                  <tr key={truck.id} className={editingId === truck.id ? "row-editing" : ""}>
                    <td className="row-index">{index + 1}</td>

                    {editingId === truck.id ? (
                      <>
                        {columns.map((c) => (
                          <td key={c.key}>
                            {c.key === "status" ? (
                              <select className="inline-select" name="status" value={editData.status} onChange={handleEditChange}>
                                {STATUS_OPTIONS.map((s) => <option key={s}>{s}</option>)}
                              </select>
                            ) : c.key === "fuelType" ? (
                              <select className="inline-select" name="fuelType" value={editData.fuelType} onChange={handleEditChange}>
                                {FUEL_OPTIONS.map((f) => <option key={f}>{f}</option>)}
                              </select>
                            ) : (
                              <input
                                className="inline-input"
                                type={c.key === "inspectionDueDate" ? "date" : c.key === "year" ? "number" : "text"}
                                name={c.key}
                                value={editData[c.key] || ""}
                                onChange={handleEditChange}
                              />
                            )}
                          </td>
                        ))}
                        <td className="row-actions">
                          <button className="btn-save-inline" onClick={() => handleEditSave(truck.id)} disabled={updating}>
                            {updating ? <i className="fas fa-spinner fa-spin"></i> : <><i className="fas fa-check"></i> Save</>}
                          </button>
                          <button className="btn-cancel-inline" onClick={handleEditCancel}>
                            <i className="fas fa-times"></i>
                          </button>
                        </td>
                      </>
                    ) : (
                      <>
                        {columns.map((c) => (
                          <td key={c.key}>
                            {c.key === "status" ? (
                              <span className={`status-badge status-${truck.status?.toLowerCase().replace(" ", "-")}`}>
                                {truck.status}
                              </span>
                            ) : c.key === "inspectionDueDate" && truck[c.key] ? (
                              <span className="date-cell">{truck[c.key]}</span>
                            ) : (
                              <span className={c.key === "amazonId" ? "cell-amazon" : ""}>
                                {truck[c.key] || <span className="cell-empty">—</span>}
                              </span>
                            )}
                          </td>
                        ))}
                        <td className="row-actions">
                          <button className="btn-edit-inline" onClick={() => handleEditClick(truck)}>
                            <i className="fas fa-pen"></i> Edit
                          </button>
                          <button className="btn-delete-inline" onClick={() => setDeleteTarget(truck)} title="Delete">
                            <i className="fas fa-trash-alt"></i>
                          </button>
                        </td>
                      </>
                    )}
                  </tr>
                ))}

                {filtered.length === 0 && !loading && (
                  <tr>
                    <td colSpan={columns.length + 2} className="no-results">No trucks found.</td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>

      </div>
    </Layout>
  );
};

export default Trucks;